import React, { useMemo } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface PasswordRule {
  id: string;
  label: string;
  test: (password: string) => boolean;
}

export const DEFAULT_PASSWORD_RULES: PasswordRule[] = [
  { id: 'length', label: '8+ characters', test: (p) => p.length >= 8 },
  { id: 'upper', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'One number', test: (p) => /\d/.test(p) },
];

function strengthScore(password: string, rules: PasswordRule[]): number {
  if (!password) return 0;
  return rules.filter((r) => r.test(password)).length;
}

interface PasswordStrengthProps {
  password: string;
  rules?: PasswordRule[];
  showMatch?: boolean;
  matchValue?: string;
  className?: string;
  /** Show rules before user types (onboarding) */
  alwaysShowRules?: boolean;
  /** After submit or blur — show unmet rules in red even if field is empty */
  showUnmetErrors?: boolean;
}

export const PasswordStrength: React.FC<PasswordStrengthProps> = ({
  password,
  rules = DEFAULT_PASSWORD_RULES,
  showMatch,
  matchValue = '',
  className,
  alwaysShowRules = true,
  showUnmetErrors = false,
}) => {
  const reduceMotion = useReducedMotion();
  const score = useMemo(() => strengthScore(password, rules), [password, rules]);
  const inValidationMode = password.length > 0 || showUnmetErrors;
  const strengthLabel =
    score === 0 ? '' : score <= 2 ? 'Weak' : score === 3 ? 'Fair' : 'Strong';
  const strengthWidth = `${(score / rules.length) * 100}%`;
  const strengthColor =
    score <= 2 ? 'bg-red-500' : score === 3 ? 'bg-amber-500' : 'bg-emerald-500';

  const passwordsMatch = showMatch && matchValue.length > 0 && password === matchValue;
  const showRules = alwaysShowRules || password.length > 0 || showUnmetErrors;

  return (
    <motion.div
      className={cn('space-y-3', className)}
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <AnimatePresence>
        {password.length > 0 ? (
          <motion.div
            key="strength-bar"
            className="space-y-1.5"
            initial={reduceMotion ? false : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            {...(reduceMotion ? {} : { exit: { opacity: 0, height: 0 } })}
            transition={{ duration: 0.25 }}
          >
            <motion.div className="h-1 overflow-hidden rounded-full bg-slate-100">
              <motion.div
                className={cn('h-full rounded-full', strengthColor)}
                initial={{ width: 0 }}
                animate={{ width: strengthWidth }}
                transition={{ duration: reduceMotion ? 0 : 0.35, ease: 'easeOut' }}
              />
            </motion.div>
            {strengthLabel ? (
              <p className="text-xs text-slate-500">
                Strength:{' '}
                <span
                  className={cn(
                    'font-medium',
                    score <= 2 ? 'text-red-600' : score === 3 ? 'text-amber-600' : 'text-emerald-600'
                  )}
                >
                  {strengthLabel}
                </span>
              </p>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {showRules ? (
        <ul className="space-y-1.5" aria-label="Password requirements">
          {rules.map((rule) => {
            const met = rule.test(password);
            const unmet = inValidationMode && !met;
            const pending = !inValidationMode && !met;

            return (
              <motion.li
                key={rule.id}
                className="flex items-center gap-2 text-sm"
                layout
                transition={{ duration: 0.2 }}
              >
                <motion.span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors',
                    met && 'bg-emerald-100 text-emerald-600',
                    unmet && 'bg-red-100 text-red-600 ring-1 ring-red-200',
                    pending && 'bg-slate-100 text-slate-400'
                  )}
                  {...(reduceMotion ? {} : { animate: { scale: met ? [1, 1.12, 1] : 1 } })}
                  transition={{ duration: 0.25 }}
                  aria-hidden
                >
                  {met ? (
                    <Check className="h-3 w-3" strokeWidth={2.5} />
                  ) : unmet ? (
                    <X className="h-3 w-3" strokeWidth={2.5} />
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-current opacity-60" />
                  )}
                </motion.span>
                <span
                  className={cn(
                    'transition-colors',
                    met && 'text-slate-700',
                    unmet && 'font-medium text-red-600',
                    pending && 'text-slate-500'
                  )}
                >
                  {rule.label}
                </span>
              </motion.li>
            );
          })}
        </ul>
      ) : null}

      <AnimatePresence>
        {showMatch && matchValue.length > 0 ? (
          <motion.p
            key="match"
            role="status"
            initial={reduceMotion ? false : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            {...(reduceMotion ? {} : { exit: { opacity: 0 } })}
            className={cn('text-sm font-medium', passwordsMatch ? 'text-emerald-600' : 'text-red-600')}
          >
            {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
          </motion.p>
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
};

export function allPasswordRulesMet(password: string, rules = DEFAULT_PASSWORD_RULES): boolean {
  return rules.every((r) => r.test(password));
}
